<script lang="ts" setup>
    import type { Column, Table } from '@tanstack/vue-table';
    import { computed, PropType } from 'vue';
    import { useI18n } from 'vue-i18n';
    import SearchInput from '@/components/SearchInput.vue';
    import ColumnFilterCheckbox from '@/components/filters/ColumnFilterCheckbox.vue';
    import ColumnFilterDate from '@/components/filters/ColumnFilterDate.vue';
    import ColumnNumberRange from '@/components/filters/ColumnNumberRange.vue';

    const { t, locale, messages } = useI18n();

    const props = defineProps({
        column: {
            type: Object as PropType<Column<any, unknown>>,
            required: true,
        },
        table: {
            type: Object as PropType<Table<any>>,
            required: true,
        },
    });

    const columnFilterValue = computed(() => props.column.getFilterValue());

    const sortedUniqueValues = computed(() => {
        const preFilteredRows = props.table.getPreFilteredRowModel().flatRows;
        const valuesSet = new Set<string>();

        for (const row of preFilteredRows) {
            const value = row.getValue(props.column.id);
            if (typeof value === 'string') {
                valuesSet.add(value);
            }
        }

        return Array.from(valuesSet).sort();
    });

    const filterType = computed(() => {
        if (props.column.columnDef.meta?.filterType) {
            return props.column.columnDef.meta.filterType;
        }

        return 'text';
    });

    const checkboxOptions = computed(() => {
        if (filterType.value === 'status') {
            return [
                { value: 1, label: t('translations.status.active') },
                { value: 0, label: t('translations.status.inactive') },
            ];
        } else if (filterType.value === 'yesNo') {
            return [
                { value: 1, label: t('forms.yes') },
                { value: 0, label: t('forms.no') },
            ];
        } else if (filterType.value === 'companyStatus') {
            return [
                { value: 'active', label: t('translations.status.active') },
                { value: 'inactive', label: t('translations.status.inactive') },
                { value: 'deleted', label: t('translations.deleted') },
            ];
        } else if (filterType.value === 'month') {
            return monthOptions.value;
        }
        return sortedUniqueValues.value.map((value) => ({
            value: value,
            label: value,
        }));
    });

    const monthOptions = computed(() => {
        const months = (messages.value[locale.value] as any).salary.sheets.months;

        return Object.entries(months).map(([key, label]) => ({
            value: Number(key),
            label: label as string,
        }));
    });

    const handleCheckboxFilterChange = (values: (string | number)[]) => {
        props.column.setFilterValue(values.length > 0 ? values : undefined);
    };
</script>

<template>
    <div>
        <ColumnFilterCheckbox
            v-if="['checkbox', 'status', 'yesNo', 'month', 'companyStatus'].includes(filterType)"
            :column-id="column.id"
            :options="checkboxOptions"
            :model-value="Array.isArray(columnFilterValue) ? columnFilterValue : []"
            @update:model-value="handleCheckboxFilterChange"
        />

        <ColumnFilterDate
            v-else-if="filterType === 'dateRange'"
            :column="column"
            :model-value="columnFilterValue as string | undefined"
            @update:model-value="(value: string | undefined) => column.setFilterValue(value)"
        />

        <ColumnNumberRange
            v-else-if="filterType === 'number' || filterType === 'currency'"
            :column="column"
            :model-value="columnFilterValue as [number | undefined, number | undefined] | undefined"
            @update:model-value="
                (value: [number | undefined, number | undefined] | undefined) =>
                    column.setFilterValue(value)
            "
        />

        <SearchInput
            v-else
            @update:modelValue="(value) => column.setFilterValue(value)"
            :placeholder="t('forms.search')"
            :modelValue="columnFilterValue as string | number"
        />
    </div>
</template>
